import { useState, useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { ConversationCard } from './components/ConversationCard';
import { ActivitiesListHeader } from './components/ActivitiesListHeader';
import AddActivityDialog from './components/AddActivityDialog';
import { LIST_ACTIVITIES } from '~/services';
import {
  ActivityResponseObject,
  ListActivitiesResponse,
} from '~/schemas/conversation';
import { useMediaQuery } from '@uidotdev/usehooks';
import { motion, AnimatePresence } from 'motion/react';
import {
  addActivities,
  updateActivity,
  selectActivitiesByAgent,
  selectNextCursor,
  selectIsAgentLoaded,
  RootState,
  clearAgentData,
} from '~/store/activities';

// Animation variants for stacked conversation cards
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08, // Faster stagger for conversation list
      delayChildren: 0.05, // Quick initial delay
    },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    x: -60, // Slide from left navigation
    scale: 0.95, // Slightly smaller initially
    rotateY: -12, // 3D rotation effect
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    rotateY: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 28,
      duration: 0.4,
    },
  },
  exit: {
    opacity: 0,
    x: -40,
    scale: 0.9,
    height: 0,
    transition: {
      duration: 0.25,
      ease: 'easeInOut',
    },
  },
  hover: {
    x: 6,
    scale: 1.02,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 25,
    },
  },
};

// Loading skeleton animations
const skeletonVariants = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const skeletonItemVariants = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 20,
    },
  },
};

export const ActivitiesList = () => {
  const { agentId = '', ...params } = useParams();
  const [isAddActivityDialogOpen, setIsAddActivityDialogOpen] = useState(false);
  const limit = 25;
  const [t] = useTranslation('conversations');
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const isSmallDevice = useMediaQuery('only screen and (max-width : 1024px)');
  const dispatch = useDispatch();
  const currentSearchTerm = searchParams.get('search');

  // --- Local State for Search ---
  const [searchResults, setSearchResults] = useState<ActivityResponseObject[]>(
    [],
  );
  const [isSearching, setIsSearching] = useState(false);
  const [searchNextCursor, setSearchNextCursor] = useState<string | null>(null);

  // --- Redux Selectors (for non-search state) ---
  const activities = useSelector((state: RootState) =>
    selectActivitiesByAgent(state, agentId),
  );
  const nextCursor = useSelector((state: RootState) =>
    selectNextCursor(state, agentId),
  );
  const isAgentLoaded = useSelector((state: RootState) =>
    selectIsAgentLoaded(state, agentId),
  );

  // --- Apollo Query ---
  // Pass LIST_ACTIVITIES first, then the options object
  const [query, { loading: queryLoading }] =
    useLazyQuery<ListActivitiesResponse>(
      LIST_ACTIVITIES, // The GraphQL document
      {
        // The options object
        onCompleted: (data) => {
          // Read the LATEST search term directly from searchParams inside the callback
          const latestSearchTerm = searchParams.get('search');
          // Safe navigation for nested properties
          const results = data?.response?.data?.results ?? [];
          const cursor = data?.response?.data?.next_cursor ?? null;

          if (latestSearchTerm) {
            // Use the latest term to decide action
            // Append to search results
            setSearchResults((prev) => {
              const existingIds = new Set(prev.map((p) => p._id));
              const uniqueNewResults = results.filter(
                (r) => !existingIds.has(r._id),
              );
              return [...prev, ...uniqueNewResults];
            });
            setSearchNextCursor(cursor);
            setIsSearching(false);
          } else {
            // Dispatch to Redux only if the response wasn't for a search that just got cleared
            if (results.length > 0) {
              dispatch(
                addActivities({
                  activities: results,
                  agentId,
                  nextCursor: cursor,
                }),
              );
            }
            // Even if no results, ensure loading state for normal list is off
            // This relies on the component using queryLoading for the normal list display
            // If `addActivities` sets loading implicitly, this might not be needed
            // Example: dispatch(setActivitiesLoading({ agentId, isLoading: false }));
          }
        },
        onError: () => {
          // Handle errors for both search and normal loading
          console.error('Error fetching activities');
          setIsSearching(false);
          // Optionally dispatch an error state to Redux
          // Example: dispatch(setActivitiesError({ agentId, error: 'Failed to load' }));
        },
        fetchPolicy: 'network-only',
      },
    );

  // Effect to trigger fetch based on search term or initial load
  useEffect(() => {
    const searchTerm = searchParams.get('search');

    if (searchTerm) {
      setIsSearching(true);
      setSearchResults([]);
      setSearchNextCursor(null);
      query({
        variables: {
          data: { agent_id: agentId, limit: limit, search: searchTerm },
        },
      });
    } else {
      setIsSearching(false);
      if (!isAgentLoaded) {
        dispatch(clearAgentData(agentId));
        query({
          variables: {
            data: { agent_id: agentId, limit: limit },
          },
        });
      }
    }
  }, [agentId, searchParams.get('search'), isAgentLoaded, query, dispatch]);

  // Effect for Infinite Scrolling
  useEffect(() => {
    const isCurrentlySearching = !!currentSearchTerm;
    const cursorToUse = isCurrentlySearching ? searchNextCursor : nextCursor;

    if (!sentinelRef.current || !cursorToUse || queryLoading || isSearching)
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          const variablesData: any = {
            agent_id: agentId,
            limit: limit,
            next_cursor: cursorToUse,
          };
          if (isCurrentlySearching) {
            variablesData.search = currentSearchTerm;
            setIsSearching(true);
          }
          query({ variables: { data: variablesData } });
        }
      },
      { root: null, rootMargin: '0px', threshold: 0.8 },
    );

    const currentSentinel = sentinelRef.current;
    observer.observe(currentSentinel);

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, [
    sentinelRef,
    queryLoading,
    isSearching,
    nextCursor,
    searchNextCursor,
    currentSearchTerm,
    agentId,
    query,
  ]);

  const handleAddNewActivity = () => {
    setIsAddActivityDialogOpen(true);
  };

  const handleActivityCreated = (newActivity: ActivityResponseObject) => {
    dispatch(updateActivity(newActivity));

    if (currentSearchTerm) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('search');
      setSearchParams(newSearchParams);
      setSearchResults([]);
      setSearchNextCursor(null);
    }
  };

  if (isSmallDevice && params['*']) {
    return null;
  }

  const isSearchView = !!currentSearchTerm;
  const listToDisplay = isSearchView ? searchResults : activities;
  const isLoading = queryLoading || (isSearchView && isSearching);
  const currentNextCursor = isSearchView ? searchNextCursor : nextCursor;

  return (
    <div className="border-base-200 w-full shrink-0 overflow-hidden border-r lg:w-[19rem]">
      <ActivitiesListHeader />
      <motion.div
        className="border-base-200 flex h-[86vh] flex-col overflow-x-hidden overflow-y-scroll border-b"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <AnimatePresence mode="popLayout">
          {listToDisplay.map((activity, index) => (
            <motion.div
              key={activity._id}
              className="border-base-300 border border-b"
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              whileHover="hover"
              layout
              custom={index}
              style={{
                // Add perspective for 3D effect
                transformStyle: 'preserve-3d',
              }}
            >
              <ConversationCard
                name={
                  (activity.first_name || activity.profile_name) ?? t('unknown')
                }
                personId={activity.person_id}
                lastUpdate={activity.updated_at}
                manualMode={activity.manual_mode}
                activityId={activity._id}
                tags={activity.tags}
              />
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Empty States with Animation */}
        {listToDisplay.length === 0 &&
          !isLoading &&
          (isSearchView ? (
            <motion.div
              className="flex h-full items-center justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  delay: 0.3,
                  type: 'spring',
                  stiffness: 300,
                  damping: 25,
                },
              }}
            >
              <p className="text-base-400 text-sm">{t('no_search_results')}</p>
            </motion.div>
          ) : (
            <motion.div
              className="flex h-full flex-col items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  delay: 0.3,
                  type: 'spring',
                  stiffness: 300,
                  damping: 25,
                },
              }}
            >
              <p className="text-base-400 text-sm">{t('no_conversations')}</p>
              <motion.button
                className="btn btn-primary rounded-full"
                onClick={handleAddNewActivity}
                whileHover={{
                  scale: 1.05,
                  transition: { duration: 0.2 },
                }}
                whileTap={{ scale: 0.95 }}
              >
                {t('add_new_activity')}
              </motion.button>
            </motion.div>
          ))}

        {/* Loading State with Staggered Animation */}
        {isLoading && listToDisplay.length === 0 && (
          <motion.div
            variants={skeletonVariants}
            initial="hidden"
            animate="visible"
          >
            {[...Array(3)].map((_, index) => (
              <motion.div key={index} variants={skeletonItemVariants}>
                <Skeleton
                  baseColor={'#F5F0EF'}
                  highlightColor={'#FFFFFF'}
                  height={80}
                />
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Loading More Indicator */}
        {isLoading && listToDisplay.length > 0 && (
          <motion.div
            className="flex justify-center p-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: {
                type: 'spring',
                stiffness: 300,
                damping: 20,
              },
            }}
          >
            <span className="loading loading-spinner loading-md"></span>
          </motion.div>
        )}

        {/* Infinite Scroll Sentinel */}
        {currentNextCursor && !isLoading && (
          <div
            ref={sentinelRef}
            className="mb-10 h-1"
            style={{ background: 'transparent' }}
          ></div>
        )}
      </motion.div>

      <AddActivityDialog
        open={isAddActivityDialogOpen}
        onClose={() => setIsAddActivityDialogOpen(false)}
        agentId={agentId}
        searchValue={currentSearchTerm || ''}
        onActivityCreated={handleActivityCreated}
      />
    </div>
  );
};
